export type RecipeHelpFieldKey =
  | 'powderBrand'
  | 'powderType'
  | 'chargeWeightGr'
  | 'primerBrand'
  | 'primerType'
  | 'brassBrand'
  | 'bulletBrand'
  | 'bulletWeightGr'
  | 'bulletType'
  | 'oalIn'
  | 'coalIn'
  | 'seatingDepthIn'
  | 'estimatedVelocityFps'
  | 'chronoData'

export interface RecipeHelpFieldContent {
  title: string
  definition: string
  examples: string[]
  safetyNote: string
}

export const RECIPE_HELP_FIELDS: RecipeHelpFieldKey[] = [
  'powderBrand',
  'powderType',
  'chargeWeightGr',
  'primerBrand',
  'primerType',
  'brassBrand',
  'bulletBrand',
  'bulletWeightGr',
  'bulletType',
  'oalIn',
  'coalIn',
  'seatingDepthIn',
  'estimatedVelocityFps',
  'chronoData'
]

export const RECIPE_HELP_CONTENT: Record<RecipeHelpFieldKey, RecipeHelpFieldContent> = {
  powderBrand: {
    title: 'Powder Brand',
    definition:
      'Manufacturer of the propellant used in this load (e.g. Hodgdon, Alliant, IMR). Record only what appears on the powder container or published manual data you verified.',
    examples: ['Hodgdon', 'Alliant', 'Vihtavuori'],
    safetyNote:
      'Never substitute powders by brand similarity alone. Always cross-check charge data in the manufacturer’s current reloading manual.'
  },
  powderType: {
    title: 'Powder Type',
    definition:
      'Specific powder product name or designation (e.g. Varget, H4350, Unique). Must match the exact powder used when developing the load.',
    examples: ['Varget', 'H4350', 'CFE BLK'],
    safetyNote:
      'Different powders are not interchangeable even within the same brand. Verify type and lot against your manual before loading.'
  },
  chargeWeightGr: {
    title: 'Charge Weight (grains)',
    definition:
      'Propellant charge in grains. Min and max reflect your tested or manual-verified range for this recipe. Use the same scale and units you used when developing the load.',
    examples: ['Min 41.5 / Max 42.0 gr (.308 with Varget)', 'Min 4.8 / Max 5.0 gr (.45 ACP)'],
    safetyNote:
      'Start at published starting loads and work up carefully. This community log is not a substitute for Hodgdon, Nosler, or other official load manuals.'
  },
  primerBrand: {
    title: 'Primer Brand',
    definition: 'Manufacturer of the primer (e.g. CCI, Federal, Winchester). Optional but helps others reproduce your setup.',
    examples: ['CCI', 'Federal', 'Winchester'],
    safetyNote: 'Use primers specified in your manual for the cartridge and powder combination. Do not mix primer types without verified data.'
  },
  primerType: {
    title: 'Primer Type',
    definition:
      'Size and type designation (e.g. CCI 400 small rifle, CCI 500 small pistol). Match what you actually used on the bench.',
    examples: ['CCI 400 (Small Rifle)', 'CCI 500 (Small Pistol)', 'Federal 210M (Large Rifle Match)'],
    safetyNote: 'Incorrect primer size or type can cause dangerous pressure or reliability issues. Verify against manual and case headstamp.'
  },
  brassBrand: {
    title: 'Brass Brand',
    definition:
      'Source or headstamp of cases (e.g. Federal, Lapua, once-fired range pickup). Optional; useful when neck tension or capacity varies by brass.',
    examples: ['Federal', 'Lapua', 'Mixed range brass (sorted)'],
    safetyNote: 'Inspect brass each firing. Document if brass is new, once-fired, or annealed so others understand your workflow.'
  },
  bulletBrand: {
    title: 'Bullet Brand',
    definition:
      'Manufacturer of the projectile (e.g. Speer, Hornady, Sierra). Record the brand on the box or manual listing for this bullet.',
    examples: ['Speer', 'Hornady', 'Sierra'],
    safetyNote: 'Bullet construction affects pressure. Do not swap brands or weights without new manual-verified charge data.'
  },
  bulletWeightGr: {
    title: 'Bullet Weight (grains)',
    definition: 'Nominal bullet weight in grains as marked on packaging or manual (e.g. 55, 168, 175).',
    examples: ['55 gr (5.56)', '168 gr (.308)', '124 gr (9mm)'],
    safetyNote: 'Heavier or lighter bullets require different charges. Never assume the same powder charge across weights.'
  },
  bulletType: {
    title: 'Bullet Type',
    definition:
      'Projectile construction category (FMJ, JHP, SP, HPBT, etc.) that matches your component. Affects bearing surface and pressure.',
    examples: ['FMJ for practice', 'HPBT for long-range', 'SP for hunting'],
    safetyNote: 'Match bullet type to intended use and manual data. Do not use pistol bullets in rifle cartridges or vice versa.'
  },
  oalIn: {
    title: 'Overall Length (OAL)',
    definition:
      'Cartridge overall length in inches, measured per your manual or tool (calipers, OAL gauge). Critical for pressure and fit in chamber/magazine.',
    examples: ['2.260 in (many 9mm loads)', '2.800 in (.308 SAAMI max reference — verify for your rifle)'],
    safetyNote:
      'Seating longer or shorter than manual specs can raise pressure or cause feed issues. Measure with your chamber/barrel when possible.'
  },
  coalIn: {
    title: 'Cartridge Overall Length (COAL)',
    definition:
      'Optional separate COAL measurement if you document it differently from OAL in your notes (some loaders track both). Often same as OAL; clarify in notes if needed.',
    examples: ['Magazine length coal for AR-15', 'Rifle coal to lands for precision'],
    safetyNote: 'If COAL differs from OAL in your workflow, explain in notes so readers do not confuse measurements.'
  },
  seatingDepthIn: {
    title: 'Seating Depth',
    definition:
      'Depth the bullet is seated into the case, often measured from case mouth to ogive or as jump to rifling. Optional; common in precision rifle loads.',
    examples: ['0.020 in off lands', '0.125 in bullet in case (pistol)'],
    safetyNote: 'Seating depth strongly affects pressure. Change only with incremental testing and manual guidance.'
  },
  estimatedVelocityFps: {
    title: 'Estimated Velocity (fps)',
    definition:
      'Expected or calculated muzzle velocity in feet per second. Optional unless you have chronograph or published data to cite.',
    examples: ['2650 fps (.308)', '1050 fps (subsonic 9mm)'],
    safetyNote: 'Estimated values are not proof of safe pressure. Prefer chronograph data when sharing performance expectations.'
  },
  chronoData: {
    title: 'Chronograph Data',
    definition:
      'Paste or summarize actual chronograph strings: velocity spreads, shot strings, temperature, barrel length if known. Strongly preferred over estimates when available.',
    examples: ['10-shot string: 2620, 2618, 2622… SD 12 fps', 'LabRadar export summary'],
    safetyNote:
      'Chrono data helps others judge consistency but does not replace manual charge limits. Always note firearm and conditions when possible.'
  }
}

export function isRecipeHelpFieldKey (value: string): value is RecipeHelpFieldKey {
  return (RECIPE_HELP_FIELDS as string[]).includes(value)
}
